import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageLoader from '../components/loaders/PageLoader';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { isPlatformIframe } from '../platform/utils/platformDetailsUtils';
import { redirectLoggedInUserToWorkspace } from '../reducers/navigationActions';
import { fetchCurrentUser } from '../user/currentUserActions';
import { resetLoggedInUserWorkspaceRedirect } from './authRouteRedirectGuard';
import {
    isCurrentUserAuthenticatedAndFetched,
    isCurrentUserFetched,
    isGuestSelector,
} from '../user/currentUserSelector';
import globalLogger from '../logger';

type AuthRouteSessionGuardProps = {
    children?: React.ReactNode;
};

// Match the fetchMe timeout
const PAGE_LOADER_TIMEOUT = 15000;

const logger = globalLogger.createChannel('auth.route-session-guard');

/**
 * Shows the auth route after the page loader has had a short window to complete.
 *
 * FIXME - WEB-16041 - This is a hack workaround for a weird issue where some users are
 *   getting stuck on the empty page loader, instead of either redirecting to their
 *   workspace or home board, or having the login form show.
 */
const TemporaryPageLoader: React.FC<AuthRouteSessionGuardProps> = ({ children }) => {
    const [hasPageLoaderTimedOut, setHasPageLoaderTimedOut] = useState(false);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            logger.error('Failed to load session before timer expired');
            setHasPageLoaderTimedOut(true);

            // After timing out a page load, clear the workspace redirect flag
            resetLoggedInUserWorkspaceRedirect();
        }, PAGE_LOADER_TIMEOUT);

        return () => window.clearTimeout(timeoutId);
    }, []);

    if (hasPageLoaderTimedOut) return <>{children}</>;

    return <PageLoader />;
};

/**
 * Restores auth for auth routes before rendering forms, then redirects sessions that already existed.
 */
const AuthRouteSessionGuard: React.FC<AuthRouteSessionGuardProps> = ({ children }) => {
    const dispatch = useDispatch();
    const hasStartedAuthCheckRef = useRef(false);

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isAuthenticated = useSelector(isCurrentUserAuthenticatedAndFetched);
    const isCurrentUserLoaded = useSelector(isCurrentUserFetched);
    const isGuest = useSelector(isGuestSelector);

    // Once this route has resolved to a guest user, authActions owns later
    // successful-auth redirects so it can preserve success_redirect/from behavior.
    const hasBeenGuestUserRef = useRef(false);

    const shouldResolveAuthState = !isCurrentUserLoaded && !isGuest && !isAuthenticated;

    useEffect(() => {
        if (!shouldResolveAuthState) return;
        if (hasStartedAuthCheckRef.current) return;

        hasStartedAuthCheckRef.current = true;
        dispatch(fetchCurrentUser());
    }, [dispatch, shouldResolveAuthState]);

    useEffect(() => {
        if (!isGuest) return;

        hasBeenGuestUserRef.current = true;
    }, [isGuest]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (hasBeenGuestUserRef.current) return;

        dispatch(redirectLoggedInUserToWorkspace());
    }, [dispatch, isAuthenticated]);

    const shouldHideIframeAuthRoute = isPlatformIframe(platformDetails);
    const shouldShowAuthRoute = !shouldHideIframeAuthRoute && !shouldResolveAuthState && !isAuthenticated;

    useEffect(() => {
        if (!shouldShowAuthRoute) return;
        // Once the user has successfully been shown the login screen, clear the redirect flag
        resetLoggedInUserWorkspaceRedirect();
    }, [shouldShowAuthRoute]);

    if (shouldHideIframeAuthRoute) return null;
    if (shouldResolveAuthState || isAuthenticated) {
        return <TemporaryPageLoader>{children}</TemporaryPageLoader>;
    }

    return <>{children}</>;
};

/**
 * Wraps auth route components with session restoration while preserving route props.
 */
export const withAuthRouteSessionGuard =
    <Props extends object>(Component: React.ComponentType<Props>): React.FC<Props> =>
    (props) => (
        <AuthRouteSessionGuard>
            <Component {...props} />
        </AuthRouteSessionGuard>
    );

export default AuthRouteSessionGuard;
